import React, { useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Stack,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    List,
    ListItem,
    Icon,
    Divider,
    useColorMode,
} from "@chakra-ui/react";
import { FiCheckCircle, FiClock, FiStar, FiGift, FiList, FiMap, FiInfo } from "react-icons/fi";
import { ChangelogEntry, RoadmapItem, CalendarModuleConfig } from "../types";
import { Tooltip } from "./Tip";
import { getColor } from "../../../brandConfig";
import { CalPageHeader, CalSurface, CalStatusPill, CalEmpty, alpha, calWords, useCalTheme } from "./ui";

interface ModuleWhatsNewProps {
    moduleConfig: CalendarModuleConfig;
}

/** Release dates are date-only strings ("2026-07-28") — read them in UTC so no viewer sees the day before. */
const formatReleaseDate = (value: string) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
};

/** a.b.c ≤ x.y.z — so a roadmap item aimed at a version already shipped never reads as still "planned for" it. */
const versionAtOrBefore = (a: string, b: string) => {
    const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
    const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const x = pa[i] ?? 0;
        const y = pb[i] ?? 0;
        if (x !== y) return x < y;
    }
    return true;
};

/**
 * ModuleWhatsNew — the calendars module's "What's new" page (module-local copy; other modules keep their own).
 *
 * Displays:
 * - What changed (version history with release notes)
 * - What's coming (roadmap organised by priority)
 */
export const ModuleWhatsNew: React.FC<ModuleWhatsNewProps> = ({ moduleConfig }) => {
    const t = useCalTheme();
    const { colorMode } = useColorMode();
    const [tabIndex, setTabIndex] = useState(0);

    const priorityConfig = {
        high: { tone: "danger" as const, label: "High priority" },
        medium: { tone: "warning" as const, label: "Medium priority" },
        low: { tone: "accent" as const, label: "Low priority" },
    };

    const statusConfig = {
        completed: { tone: "success" as const, icon: FiCheckCircle, color: t.success },
        "in-progress": { tone: "accent" as const, icon: FiClock, color: t.primary },
        planned: { tone: "neutral" as const, icon: FiStar, color: t.faint },
    };

    const changelogCount = moduleConfig.changelog?.length ?? 0;
    const roadmapCount = moduleConfig.roadmap?.length ?? 0;

    const tabStyle = {
        minH: "48px",
        fontSize: "md",
        fontWeight: "semibold",
        color: t.muted,
        _selected: { color: t.text, borderColor: t.primary },
    };

    return (
        <Box bg={getColor("background.main", colorMode)} minH="100vh">
            <Container maxW="container.xl" py={{ base: 6, md: 8 }} px={{ base: 4, md: 6 }}>
                <VStack align="stretch" spacing={6}>
                    <CalPageHeader
                        icon={FiGift}
                        eyebrow={moduleConfig.name}
                        title="What's new"
                        subtitle={moduleConfig.description}
                        badges={<CalStatusPill status="current" label={`Version ${moduleConfig.version}`} tone="accent" />}
                    />

                    <Tabs index={tabIndex} onChange={setTabIndex} variant="line">
                        <TabList borderColor={t.border} flexWrap="wrap">
                            <Tooltip label="Show every release so far, newest first. Read-only.">
                                <Tab {...tabStyle}>
                                    <Icon as={FiList} mr={2} />
                                    What changed{moduleConfig.changelog ? ` (${changelogCount})` : ""}
                                </Tab>
                            </Tooltip>
                            <Tooltip label="Show the work planned for this module. Read-only.">
                                <Tab {...tabStyle}>
                                    <Icon as={FiMap} mr={2} />
                                    What's coming{moduleConfig.roadmap ? ` (${roadmapCount})` : ""}
                                </Tab>
                            </Tooltip>
                        </TabList>

                        <TabPanels>
                            {/* CHANGELOG TAB */}
                            <TabPanel px={0}>
                                {moduleConfig.changelog && moduleConfig.changelog.length > 0 ? (
                                    <VStack align="stretch" spacing={5} mt={2}>
                                        {moduleConfig.changelog.map((entry: ChangelogEntry, idx: number) => (
                                            <CalSurface
                                                key={entry.version}
                                                borderColor={idx === 0 ? alpha(t.primary, 0.55) : t.border}
                                                borderWidth={idx === 0 ? "2px" : "1px"}
                                            >
                                                <VStack align="stretch" spacing={4}>
                                                    {/* Version header */}
                                                    <Stack direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "start", sm: "center" }} spacing={2}>
                                                        <HStack spacing={3} flexWrap="wrap">
                                                            <Heading as="h2" size="md" color={t.text}>
                                                                Version {entry.version}
                                                            </Heading>
                                                            {idx === 0 && <CalStatusPill status="current" label="Latest" tone="success" />}
                                                        </HStack>
                                                        <Text fontSize="md" color={t.muted}>
                                                            Released {formatReleaseDate(entry.releaseDate)}
                                                        </Text>
                                                    </Stack>

                                                    <Heading as="h3" size="sm" color={t.text} wordBreak="break-word">
                                                        {entry.title}
                                                    </Heading>

                                                    <Divider borderColor={t.border} />

                                                    {/* Features */}
                                                    <Box>
                                                        <Text fontSize="md" fontWeight="semibold" color={t.text} mb={3}>
                                                            What's included
                                                        </Text>
                                                        <List spacing={2}>
                                                            {entry.features.map((feature: string, featureIdx: number) => (
                                                                <ListItem key={featureIdx}>
                                                                    <HStack align="start" spacing={2}>
                                                                        <Icon as={FiCheckCircle} color={t.success} mt="5px" flexShrink={0} />
                                                                        <Text fontSize="md" color={t.text} wordBreak="break-word">{feature}</Text>
                                                                    </HStack>
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </Box>

                                                    {/* Notes */}
                                                    {entry.notes && (
                                                        <HStack
                                                            align="start"
                                                            spacing={3}
                                                            p={4}
                                                            bg={alpha(t.primary, 0.08)}
                                                            borderRadius="lg"
                                                            borderLeft="4px solid"
                                                            borderColor={t.primary}
                                                        >
                                                            <Icon as={FiInfo} color={t.primary} mt="4px" flexShrink={0} />
                                                            <Text fontSize="md" color={t.text} wordBreak="break-word">
                                                                {entry.notes}
                                                            </Text>
                                                        </HStack>
                                                    )}
                                                </VStack>
                                            </CalSurface>
                                        ))}
                                    </VStack>
                                ) : (
                                    <CalEmpty icon={FiList} title="No version history yet">
                                        Each release of this module will be listed here, with what it changed.
                                    </CalEmpty>
                                )}
                            </TabPanel>

                            {/* ROADMAP TAB */}
                            <TabPanel px={0}>
                                {moduleConfig.roadmap && moduleConfig.roadmap.length > 0 ? (
                                    <VStack align="stretch" spacing={5} mt={2}>
                                        {moduleConfig.roadmap.map((item: RoadmapItem, idx: number) => {
                                            const priority = priorityConfig[item.priority];
                                            const status = statusConfig[item.status];
                                            const targetPassed = item.targetVersion ? versionAtOrBefore(item.targetVersion, moduleConfig.version) : false;

                                            return (
                                                <CalSurface key={idx}>
                                                    <VStack align="stretch" spacing={4}>
                                                        {/* Header */}
                                                        <Stack direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "start", md: "center" }} spacing={3}>
                                                            <Heading as="h2" size="md" color={t.text} wordBreak="break-word">
                                                                {item.title}
                                                            </Heading>
                                                            <HStack spacing={2} flexWrap="wrap" rowGap={2}>
                                                                <CalStatusPill status={item.priority} label={priority.label} tone={priority.tone} />
                                                                <CalStatusPill kind="roadmap" status={item.status} tone={status.tone} />
                                                            </HStack>
                                                        </Stack>

                                                        {item.targetVersion && (
                                                            <Text fontSize="md" color={t.muted}>
                                                                {targetPassed
                                                                    ? `Was aimed at version ${item.targetVersion}; this module is now on version ${moduleConfig.version}.`
                                                                    : `Aimed at version ${item.targetVersion}.`}
                                                            </Text>
                                                        )}

                                                        {/* Description */}
                                                        <Text fontSize="md" color={t.muted}>
                                                            {item.description}
                                                        </Text>

                                                        <Divider borderColor={t.border} />

                                                        {/* Features */}
                                                        <Box>
                                                            <Text fontSize="md" fontWeight="semibold" color={t.text} mb={3}>
                                                                Planned features
                                                            </Text>
                                                            <List spacing={2}>
                                                                {item.features.map((feature: string, featureIdx: number) => (
                                                                    <ListItem key={featureIdx}>
                                                                        <HStack align="start" spacing={2}>
                                                                            <Icon
                                                                                as={status.icon}
                                                                                color={status.color}
                                                                                mt="5px"
                                                                                flexShrink={0}
                                                                                aria-label={calWords("roadmap", item.status)}
                                                                            />
                                                                            <Text fontSize="md" color={t.text} wordBreak="break-word">
                                                                                {feature}
                                                                            </Text>
                                                                        </HStack>
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Box>
                                                    </VStack>
                                                </CalSurface>
                                            );
                                        })}
                                    </VStack>
                                ) : (
                                    <CalEmpty icon={FiMap} title="No roadmap yet">
                                        Work planned for this module will be listed here.
                                    </CalEmpty>
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </VStack>
            </Container>
        </Box>
    );
};

export default ModuleWhatsNew;
